import * as React from "react"
import { useEffect } from "react"
import {
	Box, Chip, IconButton, Paper, Stack, TextField, Tooltip,
	Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography,
} from "@mui/material"
import OpenInNewIcon from '@mui/icons-material/OpenInNew'

import { BACKEND_APIS } from "@constants"
import { useAppToast } from "@ui-seeker/app"
import PageWrapper from "Pages/@PageWrapper"
import { vtPageProps } from "../pageHeader"

import { vtApi as api } from "../api"
import { tasks, isLoading, selectedDate, clearMyTasksSignals } from "./@signals"


const STATUS_COLOR = {
	pending: 'default',
	in_progress: 'warning',
	done: 'success',
	failed: 'error',
}


export default function MyTasksPage() {
	const { showToast } = useAppToast()

	const fetchAssignments = async () => {
		isLoading.value = true
		const r = await api.get(`${BACKEND_APIS.VT}/my-tasks?date=${selectedDate.value}`)
		if (!r.ok) {
			showToast.error('Could not load tasks: ' + r.error)
			tasks.value = []
		} else {
			tasks.value = r.data?.items || []
		}
		isLoading.value = false
	}

	useEffect(() => {
		fetchAssignments()
		return clearMyTasksSignals
	}, [])

	useEffect(() => {
		fetchAssignments()
	}, [selectedDate.value])

	const handleStart = async (row) => {
		const r = await api.post(`${BACKEND_APIS.VT}/tasks/${row.id_task}/start`, {})
		if (!r.ok) return showToast.error('Could not start task: ' + r.error)
		window.open(row.section.url, '_blank')
		fetchAssignments()
	}

	const items = tasks.value
	const counts = items.reduce((acc, a) => {
		acc[a.status] = (acc[a.status] || 0) + 1
		return acc
	}, {})

	return <PageWrapper
		{...vtPageProps('My Tasks', 'Your assigned tagging sessions')}
		actions={null}
	>
		<Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
			<TextField
				label="Date"
				type="date"
				size="small"
				value={selectedDate.value}
				onChange={e => { selectedDate.value = e.target.value }}
				InputLabelProps={{ shrink: true }}
			/>
			<Box sx={{ flex: 1 }} />
			<Stack direction="row" spacing={1}>
				<Chip label={`${counts.pending || 0} pending`} size="small" />
				<Chip label={`${counts.in_progress || 0} in progress`} size="small" color="warning" />
				<Chip label={`${counts.done || 0} done`} size="small" color="success" />
			</Stack>
		</Stack>

		<Paper variant="outlined">
			<TableContainer>
				<Table size="small">
					<TableHead>
						<TableRow>
							<TableCell>Retailer</TableCell>
							<TableCell>Section</TableCell>
							<TableCell>URL</TableCell>
							<TableCell>Status</TableCell>
							<TableCell align="right">Action</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{items.length === 0 && !isLoading.value ? (
							<TableRow>
								<TableCell colSpan={5} align="center" sx={{ py: 4, color: 'text.secondary' }}>
									No tasks for this date.
								</TableCell>
							</TableRow>
						) : items.map(row => (
							<TableRow key={row.id_task} hover>
								<TableCell>{row.retailer?.name}</TableCell>
								<TableCell>{row.section?.name}</TableCell>
								<TableCell>
									<Typography variant="caption" sx={{ color: 'text.secondary', wordBreak: 'break-all' }}>
										{row.section?.url}
									</Typography>
								</TableCell>
								<TableCell>
									<Chip size="small" label={row.status} color={STATUS_COLOR[row.status] || 'default'} />
								</TableCell>
								<TableCell align="right">
									<Tooltip title={row.status === 'done' ? 'Re-open in new tab' : 'Start / Open'}>
										<IconButton size="small" onClick={() => handleStart(row)}>
											<OpenInNewIcon fontSize="small" />
										</IconButton>
									</Tooltip>
								</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			</TableContainer>
		</Paper>
	</PageWrapper>
}
